export type MetroStation = {
  name: string;
  color: string; // #RRGGBB
};

export const DEFAULT_METRO_ICON_COLOR = '#E65A8C';

type MetroLine = {
  color: string;
  stations: string[];
};

function buildCityCatalog(lines: MetroLine[]): MetroStation[] {
  const seen = new Set<string>();
  const result: MetroStation[] = [];

  for (const line of lines) {
    for (const name of line.stations) {
      const key = normalizeStationName(name);
      if (seen.has(key)) continue;
      seen.add(key);
      result.push({ name, color: line.color });
    }
  }

  return result.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

function normalizeStationName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/^метро\s+/u, '');
}

/** Normalize city for lookup: trim, lower, ё→е */
export function normalizeMetroCityKey(city: string): string {
  return city.trim().toLowerCase().replace(/ё/g, 'е');
}

/** Спутники / локалити → город каталога метро. */
const METRO_CITY_ALIASES: Record<string, string> = {
  химки: 'москва',
  мытищи: 'москва',
  королев: 'москва',
  балашиха: 'москва',
  люберцы: 'москва',
  одинцово: 'москва',
  красногорск: 'москва',
  долгопрудный: 'москва',
  реутов: 'москва',
  видное: 'москва',
  щелково: 'москва',
  пушкино: 'москва',
  жуковский: 'москва',
  раменское: 'москва',
  домодедово: 'москва',
  подольск: 'москва',
  зеленоград: 'москва',
  мурино: 'санкт-петербург',
  кудрово: 'санкт-петербург',
  шушары: 'санкт-петербург',
  пушкин: 'санкт-петербург',
  колпино: 'санкт-петербург',
  петергоф: 'санкт-петербург',
  парголово: 'санкт-петербург',
  всеволожск: 'санкт-петербург',
  сертолово: 'санкт-петербург',
  бугры: 'санкт-петербург',
  'новое девяткино': 'санкт-петербург',
  девяткино: 'санкт-петербург',
};

export function resolveMetroCatalogCity(city: string): string {
  const key = normalizeMetroCityKey(city);
  return METRO_CITY_ALIASES[key] ?? key;
}

/** Stations for city, or empty array if unknown */
export function getMetroStationsForCity(city: string): MetroStation[] {
  return METRO_BY_CITY[resolveMetroCatalogCity(city)] ?? [];
}

/** Find station by name (case-insensitive, ё→е, ignore leading «метро ») */
export function findMetroStation(
  city: string,
  stationName: string,
): MetroStation | undefined {
  const key = normalizeStationName(stationName);
  return getMetroStationsForCity(city).find(
    (s) => normalizeStationName(s.name) === key,
  );
}

/** Search station across all city catalogs (for suburbs / auto-detect). */
export function findMetroStationGlobal(
  stationName: string,
): MetroStation | undefined {
  const key = normalizeStationName(stationName);
  if (!key) return undefined;
  for (const stations of Object.values(METRO_BY_CITY)) {
    const found = stations.find((s) => normalizeStationName(s.name) === key);
    if (found) return found;
  }
  return undefined;
}

/** Color from catalog or DEFAULT_METRO_ICON_COLOR */
export function resolveMetroIconColor(city: string, stationName: string): string {
  return (
    findMetroStation(city, stationName)?.color ??
    findMetroStationGlobal(stationName)?.color ??
    DEFAULT_METRO_ICON_COLOR
  );
}

/** Whether city has a curated station list */
export function hasMetroCatalog(city: string): boolean {
  return resolveMetroCatalogCity(city) in METRO_BY_CITY;
}

// Москва — Сокольническая
const MOSCOW_SOKOLNICHESKAYA: MetroLine = {
  color: '#E42313',
  stations: [
    'Бульвар Рокоссовского',
    'Черкизовская',
    'Преображенская площадь',
    'Сокольники',
    'Красносельская',
    'Комсомольская',
    'Красные Ворота',
    'Чистые пруды',
    'Лубянка',
    'Охотный Ряд',
    'Библиотека имени Ленина',
    'Кропоткинская',
    'Парк культуры',
    'Фрунзенская',
    'Спортивная',
    'Воробьёвы горы',
    'Университет',
    'Проспект Вернадского',
    'Юго-Западная',
    'Тропарёво',
    'Румянцево',
    'Саларьево',
    'Филатов Луг',
    'Прокшино',
    'Ольховая',
    'Коммунарка',
    'Потапово',
  ],
};

// Москва — Замоскворецкая
const MOSCOW_ZAMOSKVORETSKAYA: MetroLine = {
  color: '#4FB04F',
  stations: [
    'Ховрино',
    'Беломорская',
    'Речной вокзал',
    'Водный стадион',
    'Войковская',
    'Сокол',
    'Аэропорт',
    'Динамо',
    'Белорусская',
    'Маяковская',
    'Тверская',
    'Театральная',
    'Новокузнецкая',
    'Павелецкая',
    'Автозаводская',
    'Технопарк',
    'Коломенская',
    'Каширская',
    'Кантемировская',
    'Царицыно',
    'Орехово',
    'Домодедовская',
    'Красногвардейская',
    'Алма-Атинская',
  ],
};

// Москва — Арбатско-Покровская
const MOSCOW_ARBATSKO_POKROVSKAYA: MetroLine = {
  color: '#0072BA',
  stations: [
    'Пятницкое шоссе',
    'Митино',
    'Волоколамская',
    'Мякинино',
    'Строгино',
    'Крылатское',
    'Молодёжная',
    'Кунцевская',
    'Славянский бульвар',
    'Парк Победы',
    'Киевская',
    'Смоленская',
    'Арбатская',
    'Площадь Революции',
    'Курская',
    'Бауманская',
    'Электрозаводская',
    'Семёновская',
    'Партизанская',
    'Измайловская',
    'Первомайская',
    'Щёлковская',
  ],
};

// Москва — Филёвская
const MOSCOW_FILYOVSKAYA: MetroLine = {
  color: '#1EBCEF',
  stations: [
    'Кунцевская',
    'Пионерская',
    'Филёвский парк',
    'Багратионовская',
    'Фили',
    'Кутузовская',
    'Студенческая',
    'Киевская',
    'Смоленская',
    'Арбатская',
    'Александровский сад',
    'Выставочная',
    'Международная',
  ],
};

// Москва — Кольцевая
const MOSCOW_KOLTSEVAYA: MetroLine = {
  color: '#894E35',
  stations: [
    'Киевская',
    'Краснопресненская',
    'Белорусская',
    'Новослободская',
    'Проспект Мира',
    'Комсомольская',
    'Курская',
    'Таганская',
    'Павелецкая',
    'Добрынинская',
    'Октябрьская',
    'Парк культуры',
  ],
};

// Москва — Калужско-Рижская
const MOSCOW_KALUZHSKO_RIZHSKAYA: MetroLine = {
  color: '#F07E24',
  stations: [
    'Медведково',
    'Бабушкинская',
    'Свиблово',
    'Ботанический сад',
    'ВДНХ',
    'Алексеевская',
    'Рижская',
    'Проспект Мира',
    'Сухаревская',
    'Тургеневская',
    'Китай-город',
    'Третьяковская',
    'Октябрьская',
    'Шаболовская',
    'Ленинский проспект',
    'Академическая',
    'Профсоюзная',
    'Новые Черёмушки',
    'Калужская',
    'Беляево',
    'Коньково',
    'Тёплый Стан',
    'Ясенево',
    'Новоясеневская',
  ],
};

// Москва — Таганско-Краснопресненская
const MOSCOW_TAGANSKO_KRASNOPRESNENSKAYA: MetroLine = {
  color: '#8E479B',
  stations: [
    'Планерная',
    'Сходненская',
    'Тушинская',
    'Спартак',
    'Щукинская',
    'Октябрьское Поле',
    'Полежаевская',
    'Беговая',
    'Улица 1905 года',
    'Баррикадная',
    'Пушкинская',
    'Кузнецкий Мост',
    'Китай-город',
    'Таганская',
    'Пролетарская',
    'Волгоградский проспект',
    'Текстильщики',
    'Кузьминки',
    'Рязанский проспект',
    'Выхино',
    'Лермонтовский проспект',
    'Жулебино',
    'Котельники',
  ],
};

// Москва — Калининская
const MOSCOW_KALININSKAYA: MetroLine = {
  color: '#FFCD1C',
  stations: [
    'Новокосино',
    'Новогиреево',
    'Перово',
    'Шоссе Энтузиастов',
    'Авиамоторная',
    'Площадь Ильича',
    'Марксистская',
    'Третьяковская',
  ],
};

// Москва — Солнцевская
const MOSCOW_SOLNTSEVSKAYA: MetroLine = {
  color: '#FFCD1C',
  stations: [
    'Деловой центр',
    'Парк Победы',
    'Минская',
    'Ломоносовский проспект',
    'Раменки',
    'Мичуринский проспект',
    'Озёрная',
    'Говорово',
    'Солнцево',
    'Боровское шоссе',
    'Новопеределкино',
    'Рассказовка',
    'Пыхтино',
    'Аэропорт Внуково',
  ],
};

// Москва — Серпуховско-Тимирязевская
const MOSCOW_SERPUKHOVSKO_TIMIRYAZEVSKAYA: MetroLine = {
  color: '#A2A5B4',
  stations: [
    'Алтуфьево',
    'Бибирево',
    'Отрадное',
    'Владыкино',
    'Петровско-Разумовская',
    'Тимирязевская',
    'Дмитровская',
    'Савёловская',
    'Менделеевская',
    'Цветной бульвар',
    'Чеховская',
    'Боровицкая',
    'Полянка',
    'Серпуховская',
    'Тульская',
    'Нагатинская',
    'Нагорная',
    'Нахимовский проспект',
    'Севастопольская',
    'Чертановская',
    'Южная',
    'Пражская',
    'Улица Академика Янгеля',
    'Аннино',
    'Бульвар Дмитрия Донского',
  ],
};

// Москва — Люблинско-Дмитровская
const MOSCOW_LYUBLINSKO_DMITROVSKAYA: MetroLine = {
  color: '#B4D445',
  stations: [
    'Физтех',
    'Лианозово',
    'Яхромская',
    'Селигерская',
    'Верхние Лихоборы',
    'Окружная',
    'Петровско-Разумовская',
    'Фонвизинская',
    'Бутырская',
    'Марьина Роща',
    'Достоевская',
    'Трубная',
    'Сретенский бульвар',
    'Чкаловская',
    'Римская',
    'Крестьянская Застава',
    'Дубровка',
    'Кожуховская',
    'Печатники',
    'Волжская',
    'Люблино',
    'Братиславская',
    'Марьино',
    'Борисово',
    'Шипиловская',
    'Зябликово',
  ],
};

// Москва — Большая кольцевая
const MOSCOW_BOLSHAYA_KOLTSEVAYA: MetroLine = {
  color: '#82C0C0',
  stations: [
    'Савёловская',
    'Петровский парк',
    'ЦСКА',
    'Хорошёвская',
    'Народное Ополчение',
    'Мнёвники',
    'Терехово',
    'Кутузовская',
    'Деловой центр',
    'Шелепиха',
    'Давыдково',
    'Аминьевская',
    'Мичуринский проспект',
    'Проспект Вернадского',
    'Новаторская',
    'Воронцовская',
    'Зюзино',
    'Каховская',
    'Варшавская',
    'Каширская',
    'Кленовый бульвар',
    'Нагатинский Затон',
    'Печатники',
    'Текстильщики',
    'Нижегородская',
    'Авиамоторная',
    'Лефортово',
    'Электрозаводская',
    'Сокольники',
    'Рижская',
    'Марьина Роща',
  ],
};

// Москва — Бутовская
const MOSCOW_BUTOVSKAYA: MetroLine = {
  color: '#ACBFE3',
  stations: [
    'Битцевский парк',
    'Лесопарковая',
    'Улица Старокачаловская',
    'Улица Скобелевская',
    'Бульвар Адмирала Ушакова',
    'Улица Горчакова',
    'Бунинская аллея',
  ],
};

// Москва — Некрасовская
const MOSCOW_NEKRASOVSKAYA: MetroLine = {
  color: '#DE64A1',
  stations: [
    'Нижегородская',
    'Стахановская',
    'Окская',
    'Юго-Восточная',
    'Косино',
    'Улица Дмитриевского',
    'Лухмановская',
    'Некрасовка',
  ],
};

// Москва — Троицкая
const MOSCOW_TROITSKAYA: MetroLine = {
  color: '#1E3C8C',
  stations: [
    'Новаторская',
    'Университет Дружбы Народов',
    'Генерала Тюленева',
    'Тютчевская',
    'Корниловская',
    'Бачуринская',
    'Коммунарка',
  ],
};

// Санкт-Петербург — Кировско-Выборгская (1)
const SPB_LINE1: MetroLine = {
  color: '#D6083B',
  stations: [
    'Девяткино',
    'Гражданский проспект',
    'Академическая',
    'Политехническая',
    'Площадь Мужества',
    'Лесная',
    'Выборгская',
    'Площадь Ленина',
    'Чернышевская',
    'Площадь Восстания',
    'Владимирская',
    'Пушкинская',
    'Технологический институт',
    'Балтийская',
    'Нарвская',
    'Кировский завод',
    'Автово',
    'Ленинский проспект',
    'Проспект Ветеранов',
  ],
};

// Санкт-Петербург — Московско-Петроградская (2)
const SPB_LINE2: MetroLine = {
  color: '#0078C9',
  stations: [
    'Парнас',
    'Проспект Просвещения',
    'Озерки',
    'Удельная',
    'Пионерская',
    'Чёрная речка',
    'Петроградская',
    'Горьковская',
    'Невский проспект',
    'Сенная площадь',
    'Технологический институт',
    'Фрунзенская',
    'Московские ворота',
    'Электросила',
    'Парк Победы',
    'Московская',
    'Звёздная',
    'Купчино',
  ],
};

// Санкт-Петербург — Невско-Василеостровская (3)
const SPB_LINE3: MetroLine = {
  color: '#009A49',
  stations: [
    'Беговая',
    'Зенит',
    'Приморская',
    'Василеостровская',
    'Гостиный двор',
    'Маяковская',
    'Площадь Александра Невского',
    'Елизаровская',
    'Ломоносовская',
    'Пролетарская',
    'Обухово',
    'Рыбацкое',
  ],
};

// Санкт-Петербург — Правобережная (4)
const SPB_LINE4: MetroLine = {
  color: '#EA7125',
  stations: [
    'Горный институт',
    'Спасская',
    'Достоевская',
    'Лиговский проспект',
    'Площадь Александра Невского',
    'Новочеркасская',
    'Ладожская',
    'Проспект Большевиков',
    'Улица Дыбенко',
  ],
};

// Санкт-Петербург — Фрунзенско-Приморская (5)
const SPB_LINE5: MetroLine = {
  color: '#702785',
  stations: [
    'Комендантский проспект',
    'Старая Деревня',
    'Крестовский остров',
    'Чкаловская',
    'Спортивная',
    'Адмиралтейская',
    'Садовая',
    'Звенигородская',
    'Обводный канал',
    'Волковская',
    'Бухарестская',
    'Международная',
    'Проспект Славы',
    'Дунайская',
    'Шушары',
  ],
};

// Новосибирск — Ленинская
const NSK_LENINSKAYA: MetroLine = {
  color: '#D52B1E',
  stations: [
    'Заельцовская',
    'Гагаринская',
    'Красный проспект',
    'Площадь Ленина',
    'Октябрьская',
    'Речной вокзал',
    'Студенческая',
    'Площадь Маркса',
  ],
};

// Новосибирск — Дзержинская
const NSK_DZERZHINSKAYA: MetroLine = {
  color: '#009A49',
  stations: [
    'Площадь Гарина-Михайловского',
    'Сибирская',
    'Маршала Покрышкина',
    'Берёзовая роща',
    'Золотая Нива',
  ],
};

// Екатеринбург
const EKB_LINE1: MetroLine = {
  color: '#029A4A',
  stations: [
    'Проспект Космонавтов',
    'Уралмаш',
    'Машиностроителей',
    'Уральская',
    'Динамо',
    'Площадь 1905 года',
    'Геологическая',
    'Чкаловская',
    'Ботаническая',
  ],
};

// Казань
const KAZAN_CENTRAL: MetroLine = {
  color: '#009B3A',
  stations: [
    'Авиастроительная',
    'Северный вокзал',
    'Яшьлек',
    'Козья слобода',
    'Кремлёвская',
    'Площадь Габдуллы Тукая',
    'Суконная слобода',
    'Аметьево',
    'Горки',
    'Проспект Победы',
    'Дубравная',
  ],
};

// Нижний Новгород — Автозаводская
const NN_AVTOZAVODSKAYA: MetroLine = {
  color: '#CD0505',
  stations: [
    'Горьковская',
    'Московская',
    'Чкаловская',
    'Ленинская',
    'Заречная',
    'Двигатель Революции',
    'Пролетарская',
    'Автозаводская',
    'Комсомольская',
    'Кировская',
    'Парк культуры',
  ],
};

// Нижний Новгород — Сормовская
const NN_SORMOVSKAYA: MetroLine = {
  color: '#0078C9',
  stations: [
    'Стрелка',
    'Московская',
    'Канавинская',
    'Бурнаковская',
    'Буревестник',
  ],
};

// Самара
const SAMARA_LINE1: MetroLine = {
  color: '#E42313',
  stations: [
    'Алабинская',
    'Российская',
    'Московская',
    'Гагаринская',
    'Спортивная',
    'Советская',
    'Победа',
    'Безымянка',
    'Кировская',
    'Юнгородок',
  ],
};

export const METRO_BY_CITY: Record<string, MetroStation[]> = {
  москва: buildCityCatalog([
    MOSCOW_SOKOLNICHESKAYA,
    MOSCOW_ZAMOSKVORETSKAYA,
    MOSCOW_ARBATSKO_POKROVSKAYA,
    MOSCOW_FILYOVSKAYA,
    MOSCOW_KOLTSEVAYA,
    MOSCOW_KALUZHSKO_RIZHSKAYA,
    MOSCOW_TAGANSKO_KRASNOPRESNENSKAYA,
    MOSCOW_KALININSKAYA,
    MOSCOW_SOLNTSEVSKAYA,
    MOSCOW_SERPUKHOVSKO_TIMIRYAZEVSKAYA,
    MOSCOW_LYUBLINSKO_DMITROVSKAYA,
    MOSCOW_BOLSHAYA_KOLTSEVAYA,
    MOSCOW_BUTOVSKAYA,
    MOSCOW_NEKRASOVSKAYA,
    MOSCOW_TROITSKAYA,
  ]),
  'санкт-петербург': buildCityCatalog([
    SPB_LINE1,
    SPB_LINE2,
    SPB_LINE3,
    SPB_LINE4,
    SPB_LINE5,
  ]),
  новосибирск: buildCityCatalog([NSK_LENINSKAYA, NSK_DZERZHINSKAYA]),
  екатеринбург: buildCityCatalog([EKB_LINE1]),
  казань: buildCityCatalog([KAZAN_CENTRAL]),
  'нижний новгород': buildCityCatalog([NN_AVTOZAVODSKAYA, NN_SORMOVSKAYA]),
  самара: buildCityCatalog([SAMARA_LINE1]),
};
